/**
 * Loading Skeleton Component
 * Elegant skeleton screens for loading states
 */

import React from 'react';
import { Skeleton, Box, styled } from '@mui/material';
import { colors } from '../../theme/colors';

interface LoadingSkeletonProps {
  type?: 'card' | 'table' | 'metric' | 'chart' | 'text';
  rows?: number;
  height?: number | string;
}

const shimmer = `
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }
`;

const StyledSkeleton = styled(Skeleton)({
  backgroundColor: colors.neutral[100],
  '&::after': {
    background: `linear-gradient(90deg, transparent, ${colors.neutral[200]}, transparent)`,
  },
});

export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({ type = 'text', rows = 1, height }) => {
  switch (type) {
    case 'card':
      return (
        <Box sx={{ p: 3 }}>
          <StyledSkeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 2 }} />
          <StyledSkeleton variant="text" width="60%" sx={{ mb: 1 }} />
          <StyledSkeleton variant="text" width="40%" />
        </Box>
      );

    case 'table':
      return (
        <Box>
          <StyledSkeleton variant="rectangular" height={56} sx={{ borderRadius: '12px 12px 0 0', mb: 1 }} />
          {Array.from({ length: rows }).map((_, index) => (
            <StyledSkeleton key={index} variant="rectangular" height={52} sx={{ mb: 0.5 }} />
          ))}
        </Box>
      );

    case 'metric':
      return (
        <Box sx={{ p: 3 }}>
          <StyledSkeleton variant="text" width="40%" sx={{ mb: 2, fontSize: '0.875rem' }} />
          <StyledSkeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 1 }} />
          <StyledSkeleton variant="text" width="30%" />
        </Box>
      );

    case 'chart':
      return (
        <Box sx={{ p: 3 }}>
          <StyledSkeleton variant="text" width="30%" sx={{ mb: 2 }} />
          <StyledSkeleton variant="rectangular" height={height || 300} sx={{ borderRadius: 2 }} />
        </Box>
      );

    default:
      return (
        <Box>
          {Array.from({ length: rows }).map((_, index) => (
            <StyledSkeleton key={index} variant="text" sx={{ mb: 1 }} />
          ))}
        </Box>
      );
  }
};

// Shimmer effect for custom loading states
export const ShimmerBox = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: colors.neutral[100],
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, transparent, ${colors.neutral[200]}, transparent)`,
    animation: 'shimmer 2s infinite',
  },
  shimmer,
});
